import { useEffect } from 'react';
import ReactDOM from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showCloseIcon?: boolean;
  closeOnOverlayClick?: boolean;
}

const sizeMap = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

export default function Modal({
  isOpen,
  onClose,
  children,
  title,
  size = 'md',
  showCloseIcon = true,
  closeOnOverlayClick = true,
}: ModalProps) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', onKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm"
      onClick={() => closeOnOverlayClick && onClose()}
      aria-modal="true"
      role="dialog"
    >
      <div
        className={`relative w-full ${sizeMap[size]} mx-auto bg-white rounded-2xl shadow-lg p-6 transition-all duration-300 transform`}
        onClick={(e) => e.stopPropagation()}
      >
        {showCloseIcon && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-900 transition"
          >
            <span className="sr-only">Close</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
        {title && <h2 className="text-title font-semibold mb-4 text-zinc-900">{title}</h2>}
        <div className="text-zinc-700">{children}</div>
      </div>
    </div>,
    document.body
  );
}
